/*
 * @Description: 地图点位 — F1「地点搜索」数据 hook（添加/编辑点位时按名称搜地点取坐标）。
 *   防抖 300ms；mock 走 MOCK_GEO_SEARCH；用 seq 守卫丢弃过期响应。
 */
import { useCallback, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { geoSearch } from '../../../actions/locationCockpit'
import { isCockpitMockEnabled } from '../constants'
import { MOCK_GEO_SEARCH } from '../mock/geo.mock'
import { CockpitEnvelope } from '../types/cockpit.types'
import { GeoSearchData, GeoSearchItem } from '../types/poi.types'

export interface UseGeoSearchResult {
  results: GeoSearchItem[]
  loading: boolean
  /** 触发搜索（内部防抖） */
  search: (keyword: string) => void
  /** 清空结果 */
  clear: () => void
}

export function useGeoSearch(city?: string): UseGeoSearchResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [results, setResults] = useState<GeoSearchItem[]>([])
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<number | undefined>(undefined)
  const seqRef = useRef(0)

  const search = useCallback(
    (keyword: string) => {
      const kw = keyword.trim()
      window.clearTimeout(timerRef.current)
      if (!kw) {
        setResults([])
        return
      }
      if (isMock) {
        timerRef.current = window.setTimeout(() => {
          setResults(MOCK_GEO_SEARCH.items.filter((i) => i.name.includes(kw)))
        }, 200)
        return
      }
      timerRef.current = window.setTimeout(() => {
        const seq = ++seqRef.current
        setLoading(true)
        dispatch(geoSearch({ keyword: kw, city }))
          .then((res: CockpitEnvelope<GeoSearchData>) => {
            if (seq !== seqRef.current) return
            setLoading(false)
            if (res && res.code === 200) setResults(res.data?.items ?? [])
          })
          .catch(() => {
            if (seq === seqRef.current) setLoading(false)
          })
      }, 300)
    },
    [dispatch, city, isMock]
  )

  const clear = useCallback(() => setResults([]), [])

  return { results, loading, search, clear }
}
